import React from "react";
import { useNavigate } from "react-router-dom";
import { logAnalyticsEvent } from "../../engagement/Amplitude";
import { Task } from "../models/Task";
import TagBadge from "./TagBadge";
import { generateSlug } from "../utils/TaskUtils";

interface TaskTileProps {
  task: Task;
  draft?: boolean;
}

const TaskTile: React.FC<TaskTileProps> = ({ task, draft = false }) => {
  const navigate = useNavigate();

  return (
    <div
      className="bg-base-100 shadow-md hover:shadow-lg hover:shadow-secondary 
    transition-shadow duration-300 mb-4 p-2"
    >
      <div
        className="cursor-pointer text-ellipsis overflow-hidden"
        onClick={() => {
          logAnalyticsEvent("Click On Task", {
            questionId: task.id,
            draft: draft,
          });

          if (draft) {
            navigate(`/snippet/me/drafts/edit/${task.id}`);
          } else {
            navigate(`/snippet/${task.id}/${generateSlug(task.title ?? "")}`);
          }
        }}
      >
        <div className="flex shrink flex-col md:flex-row gap-0">
          {!draft && (
            <div className="w-20 md:w-24 flex flex-row md:flex-col items-stretch mr-4 gap-2 mb-2 md:md-0">
              <div className="flex-none badge badge-sm md:badge-md badge-outline line-clamp-1 w-full">
                {task.vote_total ?? 0}{" "}
                {task.vote_total === 1 ? "Vote" : "Votes"}
              </div>
              {task.similarity && (
                <div
                  className={`flex-none badge badge-sm md:badge-md badge-outline line-clamp-1 w-full`}
                >
                  {task.similarity.toFixed(2)}{" "}
                </div>
              )}
            </div>
          )}

          <div className="flex-1">
            <div className="flex items-center justify-between">
              <h2 className="card-title link-hover">{task.title}</h2>
            </div>
            <div className="card-actions mt-2">
              {task.tags?.slice(0, 5).map((tag, index) => (
                <TagBadge
                  key={index}
                  tag={tag}
                />
              ))}
              {(task.tags ?? []).length > 5 && (
                <div
                  className="badge badge-neutral cursor-pointer hover:text-white hover:bg-primary"
                  onClick={() => {
                    logAnalyticsEvent("Click On More Tags", {
                      questionId: task.id,
                    });
                    navigate(
                      `/snippet/${task.id}/${generateSlug(task.title ?? "")}`
                    );
                  }}
                >
                  +{task.tags!.length - 5} more
                </div>
              )}
              <div className="grow"></div>
            </div>
            <div className="text-sm text-base-content mt-2 font-thin">
              Asked on {new Date(task.created_at).toLocaleDateString()} at{" "}
              {new Date(task.created_at).toLocaleTimeString(undefined, {
                hour: "numeric",
                minute: "2-digit",
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskTile;
